'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { hasAccess, type AccessLevel, type AccessMap, type AdminModule } from '@ciptaimaji/shared';
import { api, type UserDto } from '@/lib/api';

interface AuthState {
  user: UserDto | null;
  loading: boolean;
  /** null selama dimuat, atau untuk akun tanpa akses panel. */
  access: AccessMap | null;
  /** Hanya untuk menyembunyikan kontrol; server tetap yang menegakkan. */
  can: (module: AdminModule, level?: AccessLevel) => boolean;
  refreshAccess: () => Promise<void>;
  login: (payload: { username: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: false,
  access: null,
  can: () => false,
  refreshAccess: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [access, setAccess] = useState<AccessMap | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAccess = useCallback(async (who: UserDto | null): Promise<void> => {
    if (!who || who.role === 'CUSTOMER') {
      setAccess(null);
      return;
    }
    try {
      setAccess((await api.access.mine()).permissions);
    } catch {
      setAccess(null);
    }
  }, []);

  useEffect(() => {
    api.auth
      .me()
      .then(async (who) => {
        setUser(who);
        await loadAccess(who);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [loadAccess]);

  const login = useCallback(
    async (payload: { username: string; password: string }): Promise<void> => {
      const who = await api.auth.login(payload);
      await loadAccess(who);
      setUser(who);
    },
    [loadAccess],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.auth.logout();
    } finally {
      setUser(null);
      setAccess(null);
    }
  }, []);

  const refreshAccess = useCallback(() => loadAccess(user), [loadAccess, user]);

  const can = useCallback(
    (module: AdminModule, level: AccessLevel = 'VIEW') => hasAccess(access?.[module], level),
    [access],
  );

  const value = useMemo(
    () => ({ user, loading, access, can, refreshAccess, login, logout }),
    [user, loading, access, can, refreshAccess, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
